import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './Banking.css';

const TABS = ['Banks', 'Bank Statement', 'Profit & Loss', 'Balance Sheet'];

export default function Banking() {
  const [tab, setTab] = useState('Banks');
  return (
    <div className="banking-page">
      <div className="page-header"><h2 className="page-title">Banking & Financials</h2></div>

      <div className="bk-tabs">
        {TABS.map(t => (
          <button key={t} className={`bk-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      <div className="bk-body">
        {tab === 'Banks'          && <BanksTab />}
        {tab === 'Bank Statement' && <StatementTab />}
        {tab === 'Profit & Loss'  && <PnLTab />}
        {tab === 'Balance Sheet'  && <BalanceSheetTab />}
      </div>
    </div>
  );
}

const inr = (n) => '₹ ' + fmtNum(n);

/* ═══════════════════════════════════════════════════════════
   1. BANKS — list, register, edit, set default
═══════════════════════════════════════════════════════════ */
const EMPTY_BANK = {
  name: '', bank_name: '', account_no: '', ifsc: '', branch: '',
  account_type: 'Current', opening_balance: '', opening_date: today(),
  is_default: false,
};

function BanksTab() {
  const { data, loading, error, reload } = useApi(
    () => api.banks.list(), [],
    { banks: [], total_balance: 0, total_in: 0, total_out: 0, count: 0 }
  );
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_BANK);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const startEdit = (b) => {
    setForm({
      name: b.name || '', bank_name: b.bank_name || '',
      account_no: b.account_no || '', ifsc: b.ifsc || '',
      branch: b.branch || '', account_type: b.account_type || 'Current',
      opening_balance: b.opening_balance || 0,
      opening_date: b.opening_date || today(),
      is_default: !!b.is_default,
    });
    setEditId(b.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { notify('❌ Bank name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, opening_balance: parseFloat(form.opening_balance || 0) };
      if (editId) await api.banks.update(editId, payload);
      else        await api.banks.create(payload);
      notify(editId ? '✅ Bank updated' : '✅ Bank registered');
      setShowForm(false); setForm(EMPTY_BANK); setEditId(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const setDefault = async (id) => {
    try { await api.banks.setDefault(id); notify('✅ Default bank set'); reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };

  const handleDelete = async () => {
    try { await api.banks.remove(confirm.id); notify('✅ Deleted'); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  return (
    <div className="bk-section">
      {msg && <div className="bk-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {!loading && (
        <div className="bk-kpis">
          <Kpi label="Total Balance" value={inr(data.total_balance)} tone="green" />
          <Kpi label="Total Money In" value={inr(data.total_in)} tone="blue" />
          <Kpi label="Total Money Out" value={inr(data.total_out)} tone="orange" />
          <Kpi label="Active Banks" value={data.count} />
        </div>
      )}

      <div className="bk-actions">
        <button className="btn btn-primary btn-sm"
                onClick={() => { setShowForm(!showForm); setForm(EMPTY_BANK); setEditId(null); }}>
          {showForm ? '✕ Cancel' : '＋ Register Bank'}
        </button>
      </div>

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? 'Edit Bank' : 'Register New Bank'}</div>
          <div className="bk-form-grid">
            <div className="form-group"><label>Display Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="HDFC Current" /></div>
            <div className="form-group"><label>Bank Name</label>
              <input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="HDFC Bank Ltd" /></div>
            <div className="form-group"><label>Account No</label>
              <input value={form.account_no} onChange={e => setForm({ ...form, account_no: e.target.value })} /></div>
            <div className="form-group"><label>IFSC</label>
              <input value={form.ifsc} onChange={e => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} /></div>
            <div className="form-group"><label>Branch</label>
              <input value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} /></div>
            <div className="form-group"><label>Account Type</label>
              <select value={form.account_type} onChange={e => setForm({ ...form, account_type: e.target.value })}>
                <option>Current</option><option>Savings</option><option>OD</option><option>CC</option><option>Cash</option>
              </select></div>
            <div className="form-group"><label>Opening Balance</label>
              <input type="number" step="0.01" value={form.opening_balance}
                     onChange={e => setForm({ ...form, opening_balance: e.target.value })} /></div>
            <div className="form-group"><label>Opening Date</label>
              <input type="date" value={form.opening_date}
                     onChange={e => setForm({ ...form, opening_date: e.target.value })} /></div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_default}
                       onChange={e => setForm({ ...form, is_default: e.target.checked })} />
                Set as default bank
              </label></div>
          </div>
          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ Saving...' : (editId ? 'Update' : 'Register')}
            </button>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">Registered Banks</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>Account</th>
                <th style={{ textAlign: 'left' }}>IFSC / Branch</th>
                <th style={{ textAlign: 'left' }}>Type</th>
                <th style={{ textAlign: 'right' }}>Opening</th>
                <th style={{ textAlign: 'right' }}>In</th>
                <th style={{ textAlign: 'right' }}>Out</th>
                <th style={{ textAlign: 'right' }}>Balance</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.banks.length === 0 ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  No banks registered yet. Click "＋ Register Bank" above.
                </td></tr>
              ) : data.banks.map(b => (
                <tr key={b.id}>
                  <td style={{ textAlign: 'left', fontWeight: 600 }}>
                    {b.name}
                    {Number(b.is_default) === 1 && <span className="pill-default">DEFAULT</span>}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>{b.bank_name}</div>
                  </td>
                  <td style={{ textAlign: 'left', fontFamily: 'monospace' }}>{b.account_no || '—'}</td>
                  <td style={{ textAlign: 'left' }}>
                    <div>{b.ifsc || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{b.branch || ''}</div>
                  </td>
                   <td style={{ textAlign: 'left' }}>{b.account_type}</td>
                  <td style={{ textAlign: 'right' }}>{inr(b.opening_balance)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--secondary)' }}>{inr(b.total_in)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--warning)' }}>{inr(b.total_out)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{inr(b.current_balance)}</td>
                  <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm" onClick={() => startEdit(b)}>✏️</button>
                    {Number(b.is_default) !== 1 && (
                      <button className="btn btn-sm" onClick={() => setDefault(b.id)} title="Set as default">⭐</button>
                    )}
                    <button className="btn btn-sm"
                            style={{ color: 'var(--danger)' }}
                            onClick={() => setConfirm(b)} title="Delete">🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Delete Bank?"
        message={confirm ? `Delete "${confirm.name}"? This can only be done if the balance is zero.` : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel="Delete"
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   2. BANK STATEMENT — per-bank running balance
═══════════════════════════════════════════════════════════ */
function StatementTab() {
  const [bankId, setBankId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today());

  React.useEffect(() => {
    const d = new Date();
    const prev = new Date(d.getTime() - 30 * 86400 * 1000);
    setFrom(prev.toISOString().slice(0, 10));
  }, []);

  const { data: banks } = useApi(() => api.banks.list(), [], { banks: [] });
  const { data, loading, error, reload } = useApi(
    () => bankId && from && to ? api.banks.statement(bankId, from, to)
                                : Promise.resolve({ rows: [], opening_balance: 0, closing_balance: 0, total_in: 0, total_out: 0, bank: {} }),
    [bankId, from, to],
    { rows: [], opening_balance: 0, closing_balance: 0, total_in: 0, total_out: 0, bank: {} }
  );

  return (
    <div className="bk-section">
      <div className="bk-filters">
        <div className="form-group"><label>Bank</label>
          <select value={bankId} onChange={e => setBankId(e.target.value)}>
            <option value="">-- Select Bank --</option>
            {(banks.banks || []).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select></div>
        <div className="form-group"><label>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <button className="btn btn-primary btn-sm" onClick={reload} disabled={!bankId}>Load</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {!bankId ? (
        <div className="bk-hint">👆 Select a bank to view its statement</div>
      ) : loading ? <Spinner /> : (
        <>
          <div className="bk-kpis">
            <Kpi label="Opening Balance" value={inr(data.opening_balance)} />
            <Kpi label="Total In"        value={inr(data.total_in)}        tone="green" />
            <Kpi label="Total Out"       value={inr(data.total_out)}       tone="orange" />
            <Kpi label="Closing Balance" value={inr(data.closing_balance)} tone="blue" />
          </div>

          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header">
              {data.bank?.name} · {data.bank?.account_no || '—'} · {from} to {to}
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'left' }}>Source</th>
                  <th style={{ textAlign: 'left' }}>Party</th>
                   <th style={{ textAlign: 'left' }}>Mode</th>
                  <th style={{ textAlign: 'left' }}>Ref</th>
                  <th style={{ textAlign: 'right' }}>Credit (In)</th>
                  <th style={{ textAlign: 'right' }}>Debit (Out)</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                 <tr style={{ background: '#f5f9fc', fontWeight: 600 }}>
                  <td colSpan="7" style={{ textAlign: 'right' }}>Opening Balance:</td>
                  <td style={{ textAlign: 'right' }}>{inr(data.opening_balance)}</td>
                </tr>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No transactions in this period</td></tr>
                ) : data.rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'left' }}>{r.entry_date}</td>
                    <td style={{ textAlign: 'left' }}>{r.reference_type}</td>
                     <td style={{ textAlign: 'left' }}>{r.party_name || '—'}</td>
                    <td style={{ textAlign: 'left' }}>{r.mode || '—'}</td>
                    <td style={{ textAlign: 'left' }}>{r.reference_no || '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--secondary)' }}>{r.type === 'Debit'  ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right', color: 'var(--warning)' }}>{r.type === 'Credit' ? inr(r.amount) : '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{inr(r.running_balance)}</td>
                  </tr>
                ))}
                 <tr style={{ background: '#f5f9fc', fontWeight: 700 }}>
                  <td colSpan="5" style={{ textAlign: 'right' }}>Totals:</td>
                  <td style={{ textAlign: 'right', color: 'var(--secondary)' }}>{inr(data.total_in)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--warning)' }}>{inr(data.total_out)}</td>
                  <td style={{ textAlign: 'right' }}>{inr(data.closing_balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   3. PROFIT & LOSS
═══════════════════════════════════════════════════════════ */
function PnLTab() {
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState(today());

  React.useEffect(() => {
    const d = new Date();
    setFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10));
  }, []);

  const { data, loading, error, reload } = useApi(
    () => from && to ? api.financials.pnl(from, to) : Promise.resolve(null),
    [from, to]
  );

  return (
    <div className="bk-section">
      <div className="bk-filters">
        <div className="form-group"><label>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading || !data ? <Spinner /> : (
        <>
          <div className="bk-kpis">
            <Kpi label="Net Sales"      value={inr(data.revenue?.net_sales)} tone="blue" />
            <Kpi label="Total Costs"    value={inr(data.costs?.total_costs)} tone="orange" />
            <Kpi label="Gross Profit"   value={inr(data.profit?.gross_profit)} tone={Number(data.profit?.gross_profit) >= 0 ? 'green' : 'red'} />
            <Kpi label="Net Profit"     value={inr(data.profit?.net_profit)}   tone={Number(data.profit?.net_profit) >= 0 ? 'green' : 'red'} />
            <Kpi label="Net Margin"     value={`${fmtNum(data.profit?.net_margin_pct || 0)}%`} />
          </div>

          <div className="pnl-grid">
            <div className="section-card">
              <div className="section-header" style={{ background: '#e6f9ee', color: '#1e6e3b' }}>Revenue</div>
              <PnLRow label="Gross Sales"    value={data.revenue?.gross_sales} />
              <PnLRow label="Sales Returns"  value={data.revenue?.sales_returns} negative />
              <PnLRow label="Net Sales"      value={data.revenue?.net_sales} bold />
              <PnLRow label="Misc Income"    value={data.revenue?.misc_income} />
            </div>
            <div className="section-card">
              <div className="section-header" style={{ background: '#fff3e6', color: '#a0531a' }}>Costs & Expenses</div>
              <PnLRow label="Purchases"    value={data.costs?.purchases} />
              <PnLRow label="Item Loss"    value={data.costs?.item_loss} />
              <PnLRow label="Other Expenses" value={data.costs?.other_exp} />
              <PnLRow label="Total Costs"  value={data.costs?.total_costs} bold />
            </div>
          </div>

          <div className="section-card pnl-summary">
            <div className="section-header">Bottom Line — {from} to {to}</div>
            <PnLRow label="Net Sales"     value={data.revenue?.net_sales} />
            <PnLRow label="(−) Purchases" value={data.costs?.purchases} negative />
            <PnLRow label="(−) Item Loss" value={data.costs?.item_loss} negative />
            <PnLRow label="Gross Profit"  value={data.profit?.gross_profit} bold />
            <PnLRow label="(+) Misc Income"  value={data.revenue?.misc_income} />
            <PnLRow label="(−) Other Expenses" value={data.costs?.other_exp} negative />
            <PnLRow label="NET PROFIT"    value={data.profit?.net_profit} bold big />
          </div>
        </>
      )}
    </div>
  );
}

function PnLRow({ label, value, bold, big, negative }) {
  const v = Number(value || 0);
  return (
    <div className={`pnl-row ${bold ? 'pnl-bold' : ''} ${big ? 'pnl-big' : ''}`}>
      <span>{label}</span>
      <span style={{ color: negative ? 'var(--warning)' : (v < 0 ? 'var(--danger)' : 'inherit') }}>{inr(v)}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   4. BALANCE SHEET
═══════════════════════════════════════════════════════════ */
function BalanceSheetTab() {
  const [asOf, setAsOf] = useState(today());
  const { data, loading, error, reload } = useApi(
    () => api.financials.balanceSheet(asOf), [asOf]
  );

  return (
    <div className="bk-section">
      <div className="bk-filters">
        <div className="form-group"><label>As of</label>
          <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} /></div>
        <button className="btn btn-primary btn-sm" onClick={reload}>Refresh</button>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading || !data ? <Spinner /> : (
        <>
          <div className="bk-kpis">
            <Kpi label="Total Assets"      value={inr(data.assets?.total_assets)}       tone="green" />
            <Kpi label="Total Liabilities" value={inr(data.liabilities?.total_liabilities)} tone="orange" />
            <Kpi label="Owner's Equity"    value={inr(data.equity)}                     tone="blue" />
          </div>

          <div className="pnl-grid">
            <div className="section-card">
              <div className="section-header" style={{ background: '#e6f9ee', color: '#1e6e3b' }}>Assets</div>
              <PnLRow label="Cash in Hand"        value={data.assets?.cash} />
              <PnLRow label="Bank Balances"       value={data.assets?.bank} />
              <PnLRow label="Customer Receivables" value={data.assets?.customer_receivables} />
              <PnLRow label="Inventory Value"     value={data.assets?.inventory_value} />
              <PnLRow label="TOTAL ASSETS"        value={data.assets?.total_assets} bold big />
            </div>
            <div className="section-card">
              <div className="section-header" style={{ background: '#fff3e6', color: '#a0531a' }}>Liabilities & Equity</div>
              <PnLRow label="Supplier Payables"   value={data.liabilities?.supplier_payables} />
              <PnLRow label="TOTAL LIABILITIES"   value={data.liabilities?.total_liabilities} bold />
              <PnLRow label="Owner's Equity"      value={data.equity} bold />
              <PnLRow label="TOTAL L + E"         value={Number(data.liabilities?.total_liabilities || 0) + Number(data.equity || 0)} bold big />
            </div>
          </div>

          {!data.balanced && (
            <div style={{ padding: 10, background: '#fff3e6', border: '1px solid #f6a623', borderRadius: 6, fontSize: 13 }}>
              ⚠️ Balance sheet does not balance exactly. Check ledger integrity in the Audit Log.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className={`bk-kpi ${tone ? 'kpi-' + tone : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}
